// The small link row to Loom's public pages: privacy, imprint, support. Plain
// anchors, not router links, so it renders anywhere (the sign-in page is also
// rendered outside a router) and each page opens on a fresh load.

import type { JSX } from "react";

import { translate, useLocale, type MessageKey } from "../i18n/index.js";

import type { LegalLang } from "./types";

export const LEGAL_PATHS = {
  privacy: "/privacy",
  imprint: "/imprint",
  support: "/support",
} as const;

export type LegalKind = keyof typeof LEGAL_PATHS;

const LABEL: Readonly<Record<LegalKind, MessageKey>> = {
  privacy: "legal.privacy",
  imprint: "legal.imprint",
  support: "legal.support",
};

/** A legal page's address; with `lang`, pinned to that language. */
export const legalHref = (kind: LegalKind, lang?: LegalLang): string =>
  lang ? `${LEGAL_PATHS[kind]}?lang=${lang}` : LEGAL_PATHS[kind];

export function LegalLinks({
  lang,
  className = "",
}: {
  /** The language the links name and open in; the interface language if unset. */
  readonly lang?: LegalLang;
  readonly className?: string;
}): JSX.Element {
  const { locale } = useLocale();
  const shown = lang ?? locale;
  return (
    <nav
      aria-label={translate(shown, "legal.links")}
      className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-ink-subtle ${className}`}
    >
      {(Object.keys(LEGAL_PATHS) as LegalKind[]).map((kind) => (
        <a key={kind} href={legalHref(kind, lang)} className="hover:text-ink-3 hover:underline">
          {translate(shown, LABEL[kind])}
        </a>
      ))}
    </nav>
  );
}
